<template>
  <div
    v-if="!(isActionsColumn && editMode)"
    :class="[`col-span-${header.width}`]"
    v-bind="customRowBindings"
  >
    <div
      :class="{
        'flex flex-row justify-between items-center h-full': true,
        border_right: addSideBorder,
      }"
    >
      <template v-if="customColumnRender[header.key]">
        <component
          :is="getCustomComponent"
          :value="row[header.key]"
          v-bind="getComponentProps"
          :row="row"
          :extra-data="extraData"
          v-on="customListeners"
          @update="$emit('update', $event)"
          @refresh="$emit('refresh', $event)"
          :class="header.key === 'stages' ? 'justify-start' : 'justify-center'"
        />
      </template>
      <template v-else-if="isEditableCell && isEditing">
        <EditableCell
          :value="row[header.key]"
          :is-editing="true"
          :input-type="getInputType"
          :options="getOptions"
          :custom-component="getCustomEditComponent"
          :custom-props="getCustomEditProps"
          :placeholder="getPlaceholder"
          @update:value="updateCellValue"
        />
      </template>
      <template v-else>
        <div class="table_data">
          <ThemeImage
            v-if="hasPrefixImage"
            :icon="getPrefixImage"
            alt="prefix"
            class="prefix_image"
            :style="{ borderRadius: prefixImageBorderRadius }"
          />
          <div class="flex flex-row gap-2 items-center truncate">
            <ProfileIcon
              v-if="showProfileIcon"
              :data="getProfileIconData"
              :avatar="header.avatar"
              :view="header.view"
              :param="header.view && !header.avatar ? undefined : header.key"
            />
            <span
              v-if="!isExcludedHeader && !isDescriptionColumn && !isTagsColumn"
              class="truncate"
            >
              <span
                v-if="header.html && header.key === 'formatted_guests'"
                @mouseenter="handleGuestHover"
                @mouseleave="hideAttendeePopover"
                v-html="getCellText"
                class="formatted-guests-container"
                ref="guestElement"
              >
              </span>
              <span v-else-if="header.html" v-html="getCellText"></span>
              <a-tooltip v-else :title="getCellText" placement="top">
                <span
                  :class="{
                    'cursor-pointer': isClickableName,
                  }"
                  @click="handleNameClick"
                  >{{ getCellText }}</span
                >
              </a-tooltip>
              <div v-if="linkIcons(header.key, row, source).some(i => i.condition)" class="mt-1 flex flex-row gap-1 items-center justify-start">
                <div
                  v-for="(icon, index) in linkIcons(header.key, row, source).filter(i => i.condition)"
                  :key="index"
                  class="cursor-pointer"
                >
                  <a-tooltip placement="top" :title="icon.url">
                    <ThemeImage
                      v-if="icon.condition"
                      :key="index"
                      :icon="icon.src"
                      class="size-3.5"
                      @click.prevent="goToURL($event, icon.url)"
                      :alt="icon.alt"
                    />
                  </a-tooltip>
                </div>
              </div>
            </span>

            <div
              v-if="isStatusColumn"
              :class="[
                'truncate flex flex-row items-center gap-1 text-[13px]',
                'ongoing_status',
                statusProvider?.[getCellText]?.[1] || '',
              ]"
            >
              <ThemeImage
                class="size-2"
                :icon="imageNames.WHITE_DOT"
                alt="white_dot"
              />
              {{ statusProvider?.[getCellText]?.[0] || getCellText }}
            </div>
            <a-tooltip
              v-if="isSequenceStatusColumn"
              :title="row?.reason || row.reason?.reason || ''"
              placement="top"
              :disabled="!row?.reason && !row.reason?.reason"
            >
              <span
                :class="[
                  'truncate',
                  'ongoing_status',
                  outreachStatusProvider?.[getCellText]?.[1],
                ]"
              >
                {{ outreachStatusProvider?.[getCellText]?.[0] }}
              </span>
            </a-tooltip>
          </div>
          <div v-if="isTagsColumn || isArrayColumn" class="tags">
            <span
              v-for="(tag, tagIndex) in visibleTags(row[header.key])"
              :key="tagIndex"
              class="tag"
              >{{ tag }}</span
            >
            <span
              v-if="remainingTagsCount(row[header.key])"
              class="proxima_nova text-[11px] secondary_color font-normal"
              >+{{ remainingTagsCount(row[header.key]) }}</span
            >
          </div>
          <div
            v-if="isDescriptionColumn"
            class="flex flex-col gap-[2px] truncate"
          >
            <span class="text-sm font-semibold">{{
              row[header.key].name
            }}</span>
            <span
              class="text-sm font-normal secondary_color leading-4 truncate"
              >{{ row[header.key].description }}</span
            >
          </div>
          <img
            v-if="hasSuffixImage"
            :src="row[header.key]?.suffixImage"
            alt="suffix"
            class="suffix_image"
          />
          <component :is="suffixComponent" v-if="showSuffixComponent" />
        </div>
      </template>
    </div>
  </div>

  <!-- Attendee Popover -->
  <div
    v-if="showAttendeePopover"
    class="attendee-popover"
    :style="popoverStyle"
  >
    <div class="attendee-list">
      <div v-if="attendeesList.length" class="attendee-title">Attendees</div>
      <div
        v-for="(attendee, index) in attendeesList"
        :key="index"
        class="attendee-item"
      >
        {{ attendee }}
      </div>
      <div v-if="!attendeesList.length" class="no-attendees">
        No attendee information available
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, inject, ref } from "vue";
import { useStore } from "vuex";
import {
  outreachStatusProvider,
  statusProvider,
} from "@/src/constants/statusConstants.js";
import { excludedHeaders, linkIcons } from "@/src/constants/tableConstants.js";
import { goToURL } from "@/src/utils/utils.js";
import { useRouter } from "vue-router";

import ProfileIcon from "@/src/components/ProfileIcon.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import EditableCell from "@/src/components/custom_table_components/EditableCell.vue";

const props = defineProps({
  header: { type: Object, required: true },
  row: { type: Object, required: true },
  colIndex: { type: Number, required: true },
  customColumnRender: { type: Object, default: () => ({}) },
  emptyCellValue: { type: String, default: "" },
  addSideBorder: { type: Boolean, default: false },
  customRow: { type: Function },
  prefixImageBorderRadius: { type: String, default: "50%" },
  suffixComponent: { type: Object, required: false },
  suffixKey: { type: String, required: false },
  source: { type: String, default: "" },
  descriptionKey: { type: String, required: false },
  extraData: { type: Object, required: false, default: null },
  editConfig: { type: Object, default: () => ({}) },
});

const emit = defineEmits(["update", "refresh", "cell-update"]);

const store = useStore();
const storeUsers = computed(() => store.getters["users/users"] || []);

const getProfileIconData = computed(() => {
  if (props.header.view && !props.header.avatar) {
    const displayName = props.row[props.header.key];
    if (displayName && typeof displayName === "string") {
      const lowerName = displayName.toLowerCase();
      const found = storeUsers.value.find(
        (u) => u.name?.toLowerCase() === lowerName || u.email?.toLowerCase() === lowerName,
      );
      return found || { name: displayName };
    }
  }
  return props.row;
});

const rowContext = inject("editableRowContext", {
  editing: { value: false },
  formState: {},
});

const isEditing = computed(() => rowContext.editing.value);
const editMode = computed(() => rowContext.editing.value);

const getComponentProps = computed(() => {
  const customRender = props.customColumnRender[props.header.key];
  const defaultProps = { value: props.row[props.header.key] };

  if (!customRender?.props) {
    return defaultProps;
  }

  return typeof customRender.props === "function"
    ? customRender.props(props.row)
    : defaultProps;
});

const customRowBindings = computed(() => {
  if (props.headers?.length !== props.colIndex && props.customRow) {
    return props.customRow(props.row, props.colIndex);
  }
  return {};
});

const getCustomComponent = computed(() => {
  const customRender = props.customColumnRender[props.header.key];
  return customRender.component || customRender;
});

const customListeners = computed(() => {
  const customRender = props.customColumnRender[props.header.key];
  return customRender.listeners || {};
});

const hasPrefixImage = computed(() => {
  return (
    (props.row[props.header.key]?.prefixImage ||
      (props.header.prefix && props.row[props.header.prefix])) &&
    props.row[props.header.key]
  );
});

const getPrefixImage = computed(() => {
  return (
    props.row[props.header.key]?.prefixImage || props.row[props.header.prefix]
  );
});

const showProfileIcon = computed(() => {
  return props.header.view || !!props.header.avatar;
});

const isExcludedHeader = computed(() => {
  return excludedHeaders.includes(props.header.key);
});

const isDescriptionColumn = computed(() => {
  return (
    props.descriptionKey &&
    props.header.key === props.descriptionKey &&
    props.row[props.header.key]
  );
});

const isStatusColumn = computed(() => {
  return props.header.key === "status";
});

const isSequenceStatusColumn = computed(() => {
  return props.header.key === "sequence_status";
});

const isTagsColumn = computed(() => {
  return props.header.key === "tags";
});

const isArrayColumn = computed(() => {
  return Array.isArray(props.row[props.header.key]) && !isTagsColumn.value;
});

const isClickableName = computed(() => {
  return (
    props.header.clickable && typeof props.header.redirectTo === "function"
  );
});

const getCellText = computed(() => {
  if (props.emptyCellValue !== "" && props.row[props.header.key] === "") {
    return props.emptyCellValue;
  }

  const value = props.row[props.header.key];

  if (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((item) => typeof item === "string")
  ) {
    return "";
  }

  return value?.text || value;
});

const hasSuffixImage = computed(() => {
  return props.row[props.header.key]?.suffixImage;
});

const showSuffixComponent = computed(() => {
  return props.suffixKey && props.header.suffix && props.row[props.suffixKey];
});

const isEditableCell = computed(() => {
  const editableHeaders = props.editConfig?.editableHeaders || [];
  return editableHeaders.includes(props.header.key);
});

const getInputType = computed(() => {
  return props.editConfig?.inputTypes?.[props.header.key] || "text";
});

const getOptions = computed(() => {
  return props.editConfig?.options?.[props.header.key] || [];
});

const getCustomEditComponent = computed(() => {
  return props.editConfig?.customComponents?.[props.header.key] || null;
});

const getCustomEditProps = computed(() => {
  return props.editConfig?.customProps?.[props.header.key] || {};
});

const getPlaceholder = computed(() => {
  return (
    props.editConfig?.placeholders?.[props.header.key] ||
    `Enter ${props.header.label}`
  );
});

const updateCellValue = (newValue) => {
  if (rowContext.formState) {
    rowContext.formState[props.header.key] = newValue;
  }

  emit("cell-update", {
    key: props.header.key,
    value: newValue,
    rowId: props.row.id,
  });
};

const visibleTags = (tags) => {
  const maxTagsToShow = 2;
  return tags?.slice(0, maxTagsToShow) || [];
};

const remainingTagsCount = (tags) => {
  const maxTagsToShow = 2;
  return tags?.length > maxTagsToShow ? tags.length - maxTagsToShow : 0;
};

const isActionsColumn = computed(() => {
  return props.header.key === "actions";
});

const guestElement = ref(null);
const showAttendeePopover = ref(false);
const attendeesList = ref([]);
const popoverStyle = ref({
  top: "0px",
  left: "0px",
});

const handleGuestHover = (event) => {
  const guestCountElement = event.target.classList.contains("guest-count")
    ? event.target
    : event.target.querySelector(".guest-count");

  if (guestCountElement) {
    try {
      const attendeesData = guestCountElement.getAttribute("data-attendees");

      if (attendeesData) {
        attendeesList.value = JSON.parse(attendeesData);

        const rect = guestCountElement.getBoundingClientRect();

        let topPosition = rect.bottom + window.scrollY + 5;
        let leftPosition = rect.left + window.scrollX;

        const estimatedPopoverWidth = 200;
        const viewportWidth = window.innerWidth;

        if (leftPosition + estimatedPopoverWidth > viewportWidth) {
          leftPosition = rect.right + window.scrollX - estimatedPopoverWidth;
        }

        leftPosition = Math.max(10, leftPosition);

        popoverStyle.value = {
          top: `${topPosition}px`,
          left: `${leftPosition}px`,
        };

        showAttendeePopover.value = true;
      }
    } catch (error) {
      console.error("Error parsing attendees data:", error);
    }
  }
};

const hideAttendeePopover = () => {
  showAttendeePopover.value = false;
  attendeesList.value = [];
};

const router = useRouter();

const handleNameClick = (event) => {
  if (!isClickableName.value) return;

  event.stopPropagation();
  event.preventDefault();

  try {
    const routeConfig = props.header.redirectTo(props.row);
    if (routeConfig) {
      router.push(routeConfig);
    }
  } catch (error) {
    console.error("Error handling name click:", error);
  }
};
</script>

<style scoped>
.table_data {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 16px 12px;
  align-self: stretch;
  overflow: hidden;
  color: var(--foreground-rgb);
  text-overflow: ellipsis;
  font-family: "Proxima Nova", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
}

.right_border {
  border-right: 1px solid var(--white-opacity-12);
}

.prefix_image,
.suffix_image {
  width: 16px;
  height: 16px;
  border-radius: 50%;
}

.tags {
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.tag {
  display: flex;
  padding: 3px 8px;
  align-items: center;
  gap: 6px;
  border-radius: 16px;
  border: 1px solid var(--white-opacity-12);
  background: var(--white-opacity-14);
  color: var(--text-color);
  text-overflow: ellipsis;
  font-family: "Proxima Nova", sans-serif;
  font-size: 11px;
  font-style: normal;
  font-weight: 400;
}

.ongoing_status {
  padding: 4px 8px;
  border-radius: 24px;
  font-weight: 600;
  cursor: pointer;
  color: white;
  background: #757575;
}

.default,
.in_active {
  background: var(--default-icon);
}

.cancelled {
  background: #fa3523;
}

.paused {
  background: var(--paused-icon);
}

.pending {
  color: var(--text-color);
}

.in_progress {
  background: #a05bd5;
}
.merged {
  background: #a05bd5;
}

.unqualified {
  background: var(--ended-icon);
}

.ended {
  background: var(--ended-icon);
}

.complete,
.active,
.sent {
  background: #2d6711;
}

.failed {
  background: #fa3523;
}

.scheduled {
  background: #c7982a;
}

.skipped {
  background: #fa3523;
}

.not_sent {
  background: #fa3523;
}

.researching {
  background: var(--researching-icon);
}

.generating {
  background: #877ae1;
}

.to_review {
  background: #3773e4;
}

.opened {
  background: #2196f3;
}

.delivered {
  background: #4caf50;
}

.clicked {
  background: #9c27b0;
}

.replied {
  background: #118456;
}

.removed {
  background: #ac1c1e;
}

.bounced {
  background: var(--bounced-icon);
}

.unsubscribed {
  background: #8e351a;
}

.attendee-popover {
  position: fixed;
  z-index: 9999;
  background: var(--dropdown-bg);
  border: 1px solid var(--white-opacity-12);
  border-radius: 8px;
  padding: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  min-width: 200px;
  max-width: 300px;
  pointer-events: none;
  opacity: 1;
  transition: opacity 0.15s ease-in-out;
}

.attendee-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.attendee-title {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 4px;
  color: var(--foreground-rgb);
}

.attendee-item {
  font-size: 13px;
  padding: 2px 0;
  color: var(--foreground-rgb);
}

.no-attendees {
  font-size: 13px;
  font-style: italic;
  color: var(--secondary-color);
}

.guest-count {
  cursor: pointer;
}
</style>
